import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CryptoAsset, formatCurrency, formatCrypto } from "@/lib/crypto-data";
import { Clock, AlertTriangle, Upload, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";

interface PurchaseModalProps {
  crypto: CryptoAsset | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = "details" | "proof" | "verifying" | "result";

const PurchaseModal = ({ crypto, isOpen, onClose, onSuccess }: PurchaseModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState<Step>("details");
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<{ approved: boolean; analysis: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!crypto) return null;

  const cryptoAmount = amount ? parseFloat(amount) / crypto.sellPrice : 0;

  const handleDetailsNext = () => {
    try {
      z.object({
        amount: z.number().min(5, "الحد الأدنى للشراء 5 دولار").max(100000000, "الحد الأقصى 100,000,000 دولار"),
      }).parse({ amount: parseFloat(amount) });
      setErrors({});
      setStep("proof");
    } catch (error) {
      if (error instanceof z.ZodError) {
        const e: Record<string, string> = {};
        error.errors.forEach((err) => { e[err.path[0]] = err.message; });
        setErrors(e);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "خطأ", description: "حجم الملف يتجاوز 5MB", variant: "destructive" });
      return;
    }
    setProofFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProofPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmitWithProof = async () => {
    if (!user || !proofFile) return;

    setStep("verifying");
    setLoading(true);

    try {
      const totalAmount = parseFloat(amount);

      // 1. Create transaction as pending (no network/wallet needed - goes to internal wallet)
      const { data: txData, error: txError } = await supabase.from("transactions").insert({
        user_id: user.id,
        crypto_symbol: crypto.symbol,
        crypto_name: crypto.name,
        amount: cryptoAmount,
        price_usd: crypto.sellPrice,
        total_usd: totalAmount,
        card_last_four: "0000",
        card_type: "internal",
        wallet_address: "internal_wallet",
        network: "internal",
        network_fee: 0,
        status: "pending",
      }).select("id").single();

      if (txError) throw txError;

      // 2. Upload proof
      const ext = proofFile.name.split(".").pop() || "jpg";
      const filePath = `${user.id}/${txData.id}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("payment-proofs")
        .upload(filePath, proofFile, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("payment-proofs").getPublicUrl(filePath);
      const proofUrl = urlData.publicUrl;

      // 3. Call AI verification
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-payment", {
        body: {
          transactionId: txData.id,
          proofUrl,
          cardLast4: "0000",
          cardType: "internal",
          totalUsd: totalAmount,
          cryptoSymbol: crypto.symbol,
        },
      });

      if (verifyError) throw verifyError;

      setVerificationResult({
        approved: verifyData.approved,
        analysis: verifyData.analysis,
      });
      setStep("result");
    } catch (error) {
      console.error("Purchase error:", error);
      toast({
        title: "فشل في إتمام العملية",
        description: "حدث خطأ. حاول مرة أخرى.",
        variant: "destructive",
      });
      setStep("proof");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAmount("");
    setErrors({}); setProofFile(null); setProofPreview(null);
    setVerificationResult(null); setStep("details");
  };

  const handleClose = () => {
    onClose();
    setTimeout(resetForm, 300);
  };

  // Result screen
  if (step === "result" && verificationResult) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="bg-card border-border max-w-md">
          <div className="flex flex-col items-center justify-center py-8 animate-scale-in">
            {verificationResult.approved ? (
              <>
                <div className="w-20 h-20 rounded-full bg-crypto-green/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-12 h-12 text-crypto-green" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">تمت الموافقة تلقائياً ✅</h3>
                <p className="text-muted-foreground text-center mb-2">
                  تحقق الذكاء الاصطناعي من إثبات الدفع ووافق على المعاملة
                </p>
                <p className="text-sm text-primary text-center">
                  تمت إضافة {formatCrypto(cryptoAmount)} {crypto.symbol} إلى محفظتك
                </p>
                <p className="text-xs text-muted-foreground mt-3 bg-secondary/50 p-2 rounded-lg">
                  {verificationResult.analysis}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
                  <Clock className="w-12 h-12 text-yellow-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">قيد المراجعة</h3>
                <p className="text-muted-foreground text-center mb-2">
                  يحتاج إثبات الدفع إلى مراجعة يدوية من الإدارة
                </p>
                <p className="text-xs text-muted-foreground mt-3 bg-secondary/50 p-2 rounded-lg">
                  {verificationResult.analysis}
                </p>
              </>
            )}
            <Button onClick={() => { handleClose(); onSuccess(); }} className="mt-6 gradient-primary text-primary-foreground">
              إغلاق
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Verifying screen
  if (step === "verifying") {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="bg-card border-border max-w-md">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-16 h-16 text-primary animate-spin mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">جاري التحقق...</h3>
            <p className="text-muted-foreground text-center text-sm">
              الذكاء الاصطناعي يتحقق من إثبات الدفع
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold"
              style={{ background: crypto.color, color: 'hsl(var(--background))' }}
            >
              {crypto.icon}
            </div>
            شراء {crypto.nameAr}
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 my-3">
          {(["details", "proof"] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                step === s ? "gradient-primary text-primary-foreground" :
                (["details", "proof"].indexOf(step) > i ? "bg-crypto-green text-white" : "bg-secondary text-muted-foreground")
              }`}>
                {i + 1}
              </div>
              {i < 1 && <div className="w-8 h-0.5 bg-border" />}
            </div>
          ))}
        </div>

        <div className="space-y-5 mt-2 pb-4">
          {/* Step 1: Amount */}
          {step === "details" && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-foreground">
                  العملات ستضاف مباشرة إلى محفظتك الداخلية بعد التحقق من إثبات الدفع.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">المبلغ (USD)</Label>
                <Input type="number" placeholder="100.00" value={amount}
                  min={5} max={100000000}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || parseFloat(val) >= 0) setAmount(val);
                  }}
                  onBlur={() => {
                    const num = parseFloat(amount);
                    if (amount && (isNaN(num) || num < 5)) setErrors(prev => ({ ...prev, amount: "الحد الأدنى 5 دولار" }));
                    else if (amount && num > 100000000) setErrors(prev => ({ ...prev, amount: "الحد الأقصى 100,000,000 دولار" }));
                    else setErrors(prev => { const { amount, ...rest } = prev; return rest; });
                  }}
                  className="bg-input border-border text-foreground text-lg" />
                <p className="text-xs text-muted-foreground">الحد الأدنى: $5 — الحد الأقصى: $100,000,000</p>
                {errors.amount && <p className="text-sm text-destructive">{errors.amount}</p>}
              </div>

              {amount && parseFloat(amount) >= 5 && (
                <div className="p-4 rounded-xl bg-primary/10 space-y-2">
                  <div className="flex justify-between text-foreground">
                    <span>المبلغ</span><span>{formatCurrency(parseFloat(amount))}</span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between text-lg font-bold text-foreground">
                      <span>ستستلم في محفظتك</span>
                      <span className="text-primary">{formatCrypto(cryptoAmount)} {crypto.symbol}</span>
                    </div>
                  </div>
                </div>
              )}

              <Button onClick={handleDetailsNext}
                disabled={!amount || parseFloat(amount) < 5}
                className="w-full h-12 gradient-primary text-primary-foreground font-semibold text-lg">
                التالي - إثبات الدفع
              </Button>
            </>
          )}

          {/* Step 2: Proof */}
          {step === "proof" && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-foreground">
                  ارفع صورة إيصال الدفع أو لقطة شاشة للمعاملة. سيقوم الذكاء الاصطناعي بالتحقق والموافقة تلقائياً.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-secondary/50 space-y-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Upload className="w-5 h-5" />
                  <span className="font-medium">إثبات الدفع</span>
                </div>

                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={handleFileSelect} />

                {proofPreview ? (
                  <div className="relative">
                    <img src={proofPreview} alt="إثبات الدفع" className="w-full rounded-lg max-h-48 object-contain bg-background" />
                    <button onClick={() => { setProofFile(null); setProofPreview(null); }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-destructive/80 text-destructive-foreground flex items-center justify-center">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary">
                    <Upload className="w-8 h-8" />
                    <span className="text-sm">اضغط لرفع صورة الإيصال</span>
                    <span className="text-xs">PNG, JPG حتى 5MB</span>
                  </button>
                )}
              </div>

              {/* Summary */}
              <div className="p-3 rounded-lg bg-secondary/30 text-sm space-y-1">
                <div className="flex justify-between text-foreground">
                  <span>المبلغ</span>
                  <span className="font-bold">{formatCurrency(parseFloat(amount))}</span>
                </div>
                <div className="flex justify-between text-foreground">
                  <span>ستضاف إلى محفظتك</span>
                  <span className="text-primary font-bold">{formatCrypto(cryptoAmount)} {crypto.symbol}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("details")} className="flex-1 h-12">
                  رجوع
                </Button>
                <Button onClick={handleSubmitWithProof}
                  disabled={!proofFile || loading}
                  className="flex-1 h-12 gradient-primary text-primary-foreground font-semibold">
                  {loading ? "جاري الإرسال..." : "إرسال للتحقق 🤖"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseModal;
